import React, { useState } from 'react';
import { useData } from '../context/DataContext';

const TrackingPage = () => {
  const { 
    targets, 
    addDailyEntry, 
    getDailyEntry, 
    debugMode, 
    debugDate, 
    setDebugMode, 
    setDebugDate, 
    getCurrentDate,
    clearTrackingData
  } = useData();
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [entries, setEntries] = useState({});

  // Initialize entries for the selected date
  React.useEffect(() => {
    const dateEntries = {};
    targets.forEach(target => {
      dateEntries[target.id] = getDailyEntry(selectedDate, target.id);
    });
    setEntries(dateEntries);
  }, [selectedDate, targets, getDailyEntry]);

  const handleEntryChange = (targetId, value) => {
    const numValue = parseInt(value) || 0;
    setEntries(prev => ({
      ...prev,
      [targetId]: numValue
    }));
    addDailyEntry(selectedDate, targetId, numValue);
  };

  const getTodayString = () => {
    return getCurrentDate();
  };

  const getDateString = (date) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isTargetCompleted = (target, entry) => {
    switch (target.type) {
      case 'daily':
        return entry >= target.amount;
      case 'weekly_frequency':
      case 'weekly_total':
        return entry > 0;
      default:
        return false;
    }
  };

  const getTargetLabel = (target) => {
    switch (target.type) {
      case 'daily':
        return `Target: ${target.amount} ${target.unit}`;
      case 'weekly_frequency':
        return `Log your session (Target for the week: ${target.frequency})`;
      case 'weekly_total':
        return `Add to weekly total (${target.amount})`;
      default:
        return '';
    }
  };

  const getQuickActions = (target) => {
    const quickValues = [];
    
    if (target.type === 'daily') {
      const targetAmount = target.amount;
      
      // Add fractions of target amount
      if (targetAmount >= 20) {
        quickValues.push(Math.floor(targetAmount / 4)); // Quarter
        quickValues.push(Math.floor(targetAmount / 2)); // Half
        quickValues.push(Math.floor(targetAmount * 0.75)); // Three quarters
      } else if (targetAmount >= 10) {
        quickValues.push(Math.floor(targetAmount / 2)); // Half
      }
      
      // Add the full target amount
      quickValues.push(targetAmount);
      
      // Add some common small increments
      if (targetAmount >= 10) {
        quickValues.push(5, 10);
      } else {
        quickValues.push(1);
      }
      
    } else if (target.type === 'weekly_frequency') {
      quickValues.push(1); // Single session
      
    } else if (target.type === 'weekly_total') {
      const targetAmount = target.amount;
      
      // Add daily suggestions based on weekly target
      quickValues.push(Math.ceil(targetAmount / 7)); // Daily average
      quickValues.push(Math.ceil(targetAmount / 5)); // Weekday average
      quickValues.push(Math.ceil(targetAmount / 4)); // Quarter of weekly
      
      // Add common increments
      if (targetAmount >= 50) {
        quickValues.push(5, 10, 25);
      } else if (targetAmount >= 20) {
        quickValues.push(5, 10);
      } else {
        quickValues.push(1, 5);
      }
    }

    // Remove duplicates and sort
    const uniqueValues = [...new Set(quickValues)]
      .map(val => Math.ceil(val))
      .filter(val => val > 0)
      .sort((a, b) => a - b);
    
    // Limit to max 6 buttons for mobile friendliness
    return uniqueValues.slice(0, 6);
  };

  if (targets.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>No targets configured</h3>
          <p>Go to the Config page to set up your fitness targets first!</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2 className="card-title">Daily Tracking</h2>
        
        {/* Debug Toggle - Only show on localhost */}
        {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          background: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
              style={{ transform: 'scale(1.2)' }}
            />
            <span style={{ fontWeight: '500', color: '#856404' }}>🐛 Debug Mode</span>
          </label>
          
          {debugMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', color: '#856404' }}>Override "Today":</label>
              <input
                type="date"
                value={debugDate}
                onChange={(e) => setDebugDate(e.target.value)}
                style={{ 
                  padding: '0.25rem 0.5rem', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
          )}
          
          {debugMode && (
            <div style={{ width: '100%' }}>
              <p style={{ 
                fontSize: '0.8rem', 
                color: '#856404', 
                margin: '0 0 1rem 0', 
                fontStyle: 'italic'
              }}>
                Debug mode allows you to test the app by changing what date is considered "today"
              </p>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete ALL tracking data? This will remove all daily entries, rewards, and punishments but keep your target configurations.')) {
                    clearTrackingData();
                    alert('All tracking data has been cleared!');
                  }
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🗑️ Clear All Tracking Data
              </button>
            </div>
          )}
        </div>
        )}
        
        <div className="form-group">
          <label className="form-label">Select Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-input"
            max={getTodayString()}
          />
          <p style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.9rem' }}>
            {getDateString(selectedDate)}
            {debugMode && (
              <span style={{ color: '#e67e22', fontWeight: '500', marginLeft: '0.5rem' }}>
                (Debug: "Today" is {getDateString(debugDate)})
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="grid">
        {targets.map(target => {
          const entry = entries[target.id] || 0;
          const isCompleted = isTargetCompleted(target, entry);
          const quickActions = getQuickActions(target);

          return (
            <div key={target.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ marginBottom: '0.25rem', color: '#2d3748' }}>{target.name}</h3>
                  <p style={{ color: '#718096', fontSize: '0.9rem' }}>
                    {getTargetLabel(target)}
                  </p>
                </div>
                {isCompleted && (
                  <span className="badge badge-success">✓ Done</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Amount {target.unit && `(${target.unit})`}
                </label>
                <input
                  type="number"
                  value={entry}
                  onChange={(e) => handleEntryChange(target.id, e.target.value)}
                  className="form-input"
                  min="0"
                  placeholder="Enter amount"
                />
              </div>

              {quickActions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#4a5568', marginBottom: '0.5rem' }}>
                    Quick add:
                  </p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(50px, 1fr))', 
                    gap: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    {quickActions.map(value => (
                      <button
                        key={value}
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => handleEntryChange(target.id, (entry + value).toString())}
                        style={{ 
                          minWidth: '50px', 
                          padding: '0.75rem 0.25rem',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          minHeight: '44px'
                        }}
                      >
                        +{value}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => handleEntryChange(target.id, target.amount.toString())}
                      style={{ 
                        flex: 1,
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        border: 'none',
                        minHeight: '44px'
                      }}
                    >
                      Target Met ({target.amount})
                    </button>
                    {entry > 0 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => handleEntryChange(target.id, '0')}
                        style={{ 
                          minWidth: '80px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          minHeight: '44px'
                        }}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              )}

              {target.type === 'daily' && (
                <div style={{ marginTop: '1rem' }}>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${Math.min((entry / target.amount) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                    {entry} / {target.amount} {target.unit} ({Math.min(Math.round((entry / target.amount) * 100), 100)}%)
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card" style={{ marginTop: '2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <h3 style={{ color: 'white', marginBottom: '1rem' }}>💡 Tips</h3>
        <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.6' }}>
          <li>Use quick add buttons for faster entry</li>
          <li>You can track past dates by changing the date selector</li>
          <li>Green checkmarks show completed daily targets</li>
          <li>Check the Progress page to see your weekly performance</li>
        </ul>
      </div>
    </div>
  );
};

export default TrackingPage;
